import * as moment from 'moment';
import * as XLSX from 'xlsx';

type ActivityCycleSummaryDTO = {
    totalOpened: number;
    totalClosed: number;
    totalReopened: number;
    totalOpenedAndClosed: number;
    totalActivity: number;
};

type SummaryLabel = {
    key: string;
    label: string;
};

class ActivitySummaryController implements angular.IOnInit, angular.IOnChanges {
    public fromDate: Date;
    public toDate: Date;
    public refreshKey: number;
    public summaryLabels: SummaryLabel[];
    public fetchData: (args: { fromDate: Date; toDate: Date }) => angular.IPromise<any>;
    public exportSheetName: string;
    public exportFilePrefix: string;
    public compact: boolean;

    public isLoading: boolean = false;
    public summaryData: ActivityCycleSummaryDTO | null = null;
    public summaryRows: any[] = [];

    private latestRequestId: number = 0;

    /** @ngInject */
    constructor(
        private notifyjs: any
    ) {}

    $onInit(): void {}

    $onChanges(changes: angular.IOnChangesObject): void {
        if ((changes.refreshKey) && this.fromDate) {
            this.reloadSummary();
        }
    }

    private getLabels(): SummaryLabel[] {
        return this.summaryLabels || [
            { key: 'totalOpened', label: 'No. of newly opened' },
            { key: 'totalClosed', label: 'No. of closed' },
            { key: 'totalReopened', label: 'No. of reopened' },
            { key: 'totalActivity', label: 'Total activity' }
        ];
    }

    private transformSummaryDataToRows(data: ActivityCycleSummaryDTO | null): void {
        if (!data) {
            this.summaryRows = [];
            return;
        }

        const labels = this.getLabels();
        this.summaryRows = labels.map(l => ({
            label: l.label,
            type: 'number',
            value: (data as any)[l.key] !== null && (data as any)[l.key] !== undefined ? (data as any)[l.key] : 'N/A'
        }));
    }

    formatSummaryValue(value: any, type: string): string {
        if (value === null || value === undefined) {
            return '-';
        }
        switch (type) {
            case 'number':
                return Number(value).toLocaleString('en-US');
            default:
                return value.toString();
        }
    }

    private reloadSummary(): void {
        if (!this.fromDate || !this.fetchData) {
            return;
        }

        const requestId = ++this.latestRequestId;
        this.isLoading = true;

        this.fetchData({ fromDate: this.fromDate, toDate: this.toDate })
            .then((r: any) => {
                if (requestId !== this.latestRequestId) return;
                const d = r && r.data ? r.data : null;
                this.summaryData = {
                    totalOpened: Number(d && d.totalOpened != null ? d.totalOpened : 0),
                    totalClosed: Number(d && d.totalClosed != null ? d.totalClosed : 0),
                    totalReopened: Number(d && d.totalReopened != null ? d.totalReopened : 0),
                    totalOpenedAndClosed: Number(d && d.totalOpenedAndClosed != null ? d.totalOpenedAndClosed : 0),
                    totalActivity: Number(d && d.totalActivity != null ? d.totalActivity : 0),
                };
                this.transformSummaryDataToRows(this.summaryData);
            })
            .catch((err: any) => {
                if (requestId !== this.latestRequestId) return;
                this.notifyjs('Failed to load summary data: ' + (err.data || err.message || err));
                this.summaryData = null;
                this.summaryRows = [];
            })
            .finally(() => {
                if (requestId !== this.latestRequestId) return;
                this.isLoading = false;
            });
    }

    exportSummaryToExcel(): void {
        if (!this.summaryRows || this.summaryRows.length === 0) {
            this.notifyjs('No summary data available to export', 'warning');
            return;
        }

        try {
            const workbook = XLSX.utils.book_new();
            const headers = ['Metric', 'Value'];

            const dataRows = this.summaryRows.map((row: any) => [
                row.label || '',
                this.formatSummaryValue(row.value, row.type)
            ]);

            const filterRow = new Date(this.fromDate).toLocaleDateString() + ' - ' + new Date(this.toDate).toLocaleDateString();
            const worksheetData = [[filterRow], [' '], headers, ...dataRows];
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            worksheet['!cols'] = [
                { wch: 30 },
                { wch: 15 }
            ];

            const sheetName = this.exportSheetName || 'Summary';
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

            const fromDateStr = this.fromDate ? moment(this.fromDate).format('YYYY-MM-DD') : '';
            const toDateStr = this.toDate ? moment(this.toDate).format('YYYY-MM-DD') : '';
            const prefix = this.exportFilePrefix || 'activity_summary';
            const fileName = prefix +
                (fromDateStr && toDateStr ? '_' + fromDateStr + '_to_' + toDateStr : '') +
                '_' + moment().format('YYYY-MM-DD') + '.xlsx';

            XLSX.writeFile(workbook, fileName);
            this.notifyjs('Summary Excel file downloaded successfully', 'success');
        } catch (error) {
            console.error('Error exporting summary to Excel:', error);
            this.notifyjs('Error exporting summary to Excel.', 'error');
        }
    }
}

export const activitySummary: angular.IComponentOptions = {
    template: require('./activity-summary.html'),
    controller: ActivitySummaryController,
    bindings: {
        fromDate: '<',
        toDate: '<',
        refreshKey: '<',
        summaryLabels: '<',
        fetchData: '<',
        exportSheetName: '@',
        exportFilePrefix: '@',
        compact: '<'
    }
};



// WEBPACK FOOTER //
// ./src/app/fees/fees-components/activity-summary/activity-summary.ts